import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, Video, Image, X, CheckCircle, AlertCircle } from 'lucide-react';
import { uploadApi, coursesApi } from '@/services/api';
import { toast } from 'sonner';

interface UploadedFile {
  id: string;
  name: string;
  type: 'video' | 'document' | 'image';
  size: number;
  url?: string;
  uploadProgress: number;
  status: 'uploading' | 'completed' | 'error';
  createdAt?: string;
  courseId?: string;
  courseTitle?: string;
}

interface CourseSelection {
  id: string;
  title: string;
  category: string;
}

// Removed mock courses - will fetch from API

export const UploadPage: React.FC = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [contentTitle, setContentTitle] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [courses, setCourses] = useState<CourseSelection[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchUploadedFiles();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await coursesApi.getAll();
      console.log('📚 Courses response:', response.data);
      
      // Handle courses like CoursesPage does
      let coursesData = [];
      if ((response.data as any).success) {
        coursesData = (response.data as any).data?.courses || (response.data as any).data || [];
      } else {
        coursesData = [];
      }
      
      console.log('📚 Courses data:', coursesData);
      
      setCourses(coursesData.map((course: any) => ({
        id: course.id,
        title: course.title,
        category: course.category?.name || 'Sin categoría'
      })));
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Error al cargar los cursos');
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      setIsLoading(true);
      const response = await uploadApi.getContent();
      if (response.data.success) {
        const contentData = response.data.data || [];
        console.log('📂 Content data:', contentData);
        
        // Extract files from content objects
        const uploadedFiles: UploadedFile[] = [];
        contentData.forEach((content: any) => {
          if (content.files && Array.isArray(content.files)) {
            content.files.forEach((file: any) => {
              uploadedFiles.push({
                id: file.id,
                name: file.originalName || file.fileName,
                type: getFileCategory(file.mimeType || file.mimetype),
                size: file.size || 0,
                url: file.url ? `https://api.escuela.agentesia.cloud${file.url}` : undefined,
                uploadProgress: 100,
                status: 'completed' as const,
                createdAt: file.createdAt || content.createdAt,
                courseId: content.courseId,
                courseTitle: content.course?.title
              });
            });
          }
        });
        
        console.log('📁 Extracted files:', uploadedFiles);
        setFiles(uploadedFiles);
      }
    } catch (error) {
      console.error('Error fetching uploaded files:', error);
      toast.error('Error al cargar los archivos');
    } finally {
      setIsLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeIcon = (type: string) => {
    if (type.startsWith('video/')) return <Video className="w-5 h-5 text-blue-500" />;
    if (type.startsWith('image/')) return <Image className="w-5 h-5 text-green-500" />;
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  const getFileCategory = (type: string): 'video' | 'document' | 'image' => {
    if (type.startsWith('video/')) return 'video';
    if (type.startsWith('image/')) return 'image';
    return 'document';
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    console.log('🔄 Files selected:', selectedFiles);
    console.log('🔄 Files count:', selectedFiles.length);
    selectedFiles.forEach((file, index) => {
      console.log(`🔄 File ${index}:`, file.name, file.size, file.type);
    });
    
    setPendingFiles(selectedFiles);
    console.log('🔄 Pending files updated:', selectedFiles);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Removed simulateUpload function - will use real upload

  const removeFile = async (fileId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este archivo?')) return;
    
    try {
      await uploadApi.deleteFile(fileId);
      toast.success('Archivo eliminado exitosamente');
      // Refrescar la lista
      await fetchUploadedFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error al eliminar el archivo');
    }
  };

  const handleUploadContent = async () => {
    if (!selectedCourse || !contentTitle || pendingFiles.length === 0) {
      toast.error('Por favor completa todos los campos requeridos y selecciona archivos');
      return;
    }

    setIsUploading(true);

    try {
      // NUEVA ESTRATEGIA: Upload usando fetch directo
      for (const file of pendingFiles) {
        const formData = new FormData();
        formData.append('courseId', selectedCourse);
        formData.append('title', `${contentTitle} - ${file.name}`);
        if (contentDescription) {
          formData.append('description', contentDescription);
        }
        formData.append('files', file);
        
        // Debug FormData
        console.log('🔍 FormData contents:');
        for (let [key, value] of formData.entries()) {
          console.log(`🔍 ${key}:`, value);
          if (value instanceof File) {
            console.log(`🔍 File details - name: ${value.name}, size: ${value.size}, type: ${value.type}`);
          }
        }
        
        const token = localStorage.getItem('token');
        const response = await fetch('https://api.escuela.agentesia.cloud/api/v1/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.message || `Error ${response.status}`);
        }
        
        if (!result.success) {
          throw new Error(result.message || 'Upload failed');
        }
      }

      toast.success(`${pendingFiles.length} archivo(s) subido(s) exitosamente`);
      
      // Reset form
      setPendingFiles([]);
      setSelectedCourse('');
      setContentTitle('');
      setContentDescription('');
      
      // Refresh the file list
      await fetchUploadedFiles();
      
    } catch (error: any) {
      console.error('❌ Upload error:', error);
      toast.error(error.message || 'Error al subir el contenido');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Subir Contenido</h1>
        <p className="text-gray-600">
          Sube videos, documentos e imágenes para tus cursos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Form */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Información del Contenido
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Curso *
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Selecciona un curso</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.title} - {course.category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título del Contenido *
                </label>
                <input
                  type="text"
                  value={contentTitle}
                  onChange={(e) => setContentTitle(e.target.value)}
                  placeholder="Ej: Introducción a Variables"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={contentDescription}
                  onChange={(e) => setContentDescription(e.target.value)}
                  placeholder="Descripción del contenido..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* File Upload Area */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Archivos
            </h2>
            
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 cursor-pointer transition-colors"
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Arrastra archivos aquí o haz clic para seleccionar
              </p>
              <p className="text-sm text-gray-500">
                Soporta videos (MP4, WebM), documentos (PDF, DOC) e imágenes (JPG, PNG)
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="*/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {/* Pending Files */}
          {pendingFiles.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Archivos Seleccionados ({pendingFiles.length})
              </h2>
              <div className="space-y-2">
                {pendingFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-3">
                      {getFileTypeIcon(file.type)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== index))}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUploadContent}
            disabled={isUploading || !selectedCourse || !contentTitle || pendingFiles.length === 0}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isUploading ? 'Subiendo...' : 'Subir Contenido'}
          </button>
        </div>

        {/* File List */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Archivos en la Plataforma ({files.length})
          </h2>
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Cargando archivos...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8">
              <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay archivos en la plataforma</p>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map(file => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {getFileTypeIcon(file.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                          {file.courseTitle && ` • ${file.courseTitle}`}
                          {file.createdAt && ` • ${new Date(file.createdAt).toLocaleDateString()}`}
                        </p>
                        {file.url && (
                          <a 
                            href={file.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Ver archivo
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                      {file.status === 'error' && (
                        <AlertCircle className="w-5 h-5 text-red-500" />
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {file.status === 'uploading' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${file.uploadProgress}%` }}
                      />
                    </div>
                  )}

                  {file.status === 'completed' && (
                    <div className="text-xs text-green-600 font-medium">
                      Subido completamente
                    </div>
                  )}

                  {file.status === 'error' && (
                    <div className="text-xs text-red-600 font-medium">
                      Error al subir
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Guidelines */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          Guías de Subida
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Videos</h4>
            <ul className="text-blue-700 space-y-1">
              <li>• Formato: MP4, WebM</li>
              <li>• Tamaño máximo: 500MB</li>
              <li>• Resolución recomendada: 1080p</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Documentos</h4>
            <ul className="text-blue-700 space-y-1">
              <li>• Formato: PDF, DOC, DOCX, PPT</li>
              <li>• Tamaño máximo: 50MB</li>
              <li>• Usa nombres descriptivos</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Imágenes</h4>
            <ul className="text-blue-700 space-y-1">
              <li>• Formato: JPG, PNG, SVG</li>
              <li>• Tamaño máximo: 10MB</li>
              <li>• Resolución mínima: 800px</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};